import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Fieldset } from 'primereact/fieldset'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'

import { tableCellCentered } from '../../../../assets/js/styles'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import { errorHandler } from '../../../../services/util/error.service'
import { isRouteAllowed } from '../../../../services/permission/menus'
import * as CarregamentoHttp from '../../../../services/api/https/deliveries/carregamento.http'
import {
  Btn,
  Cnpj,
  CustomCalendar,
  CustomCardColorBar,
  CustomCheckbox,
  CustomDataTableHeader,
  CustomDropdown,
  CustomMultiSelect,
  CustomPanel,
  CustomTriStateCheckbox,
  InputText,
  Loading,
  NumeroDecimal,
  PageHeader,
  Periodo,
  ThreeDots,
} from '../../../../components'
import { gerarArquivoExcel } from '../../../../services/util/excel.service'
import { getPeriodoUltimos30Dias, parseToMysqlFormat } from '../../../../services/util/date.service'
import ModalEditarCarregamento from './ModalEditarCarregamento'
import ModalTentativaReentrega from '../RetornoVeiculoApuracao/ModalTentativaReentrega/ModalTentativaReentrega'
import { azul_escuro, azul_hover, gray, laranja, pink, vinho } from '../../../../assets/js/colors'
import {
  getEnviosParaFrota,
  postRoutesDistance,
} from '../../../../services/api/https/deliveries/envio-rota.http'
import CustomCardDistance from '../../../../components/CustomCardDistance/CustomCardDistance'
import { useCarregamentoVeiculoContext } from '../../../../contexts/EntregaContext'
import useShowReleaseStatus from '../useShowReleaseStatus'
import { getZonasEntrega } from '../../../../services/api/https/registration/zona-entrega.http'
import {
  buildZonasEntregaOptions,
  buildCityIdsFromZonas,
} from '../../../../services/util/zona-entrega.service'
import {
  buildAgendamentosOptions,
  matchAgendamentoSelecionado,
} from '../../../../services/util/agendamento-entrega.service'

const ESTADOS = {
  AC: 'ACRE',
  AL: 'ALAGOAS',
  AP: 'AMAPÁ',
  AM: 'AMAZONAS',
  BA: 'BAHIA',
  CE: 'CEARÁ',
  DF: 'DISTRITO FEDERAL',
  ES: 'ESPÍRITO SANTO',
  GO: 'GOIÁS',
  MA: 'MARANHÃO',
  MT: 'MATO GROSSO',
  MS: 'MATO GROSSO DO SUL',
  MG: 'MINAS GERAIS',
  PA: 'PARÁ',
  PB: 'PARAÍBA',
  PR: 'PARANÁ',
  PE: 'PERNAMBUCO',
  PI: 'PIAUÍ',
  RJ: 'RIO DE JANEIRO',
  RN: 'RIO GRANDE DO NORTE',
  RS: 'RIO GRANDE DO SUL',
  RO: 'RONDÔNIA',
  RR: 'RORAIMA',
  SC: 'SANTA CATARINA',
  SP: 'SÃO PAULO',
  SE: 'SERGIPE',
  TO: 'TOCANTINS',
}

const LOAD_MESSAGE = {
  NONE: 'NONE',
  SALVAR_CARREGAMENTO: 'Carregando os Veículos ....',
  SUCCESS: 'Carregamento salvo com sucesso',
}

// Monta o texto da coluna unificada Cidade (UF) - ex: VITÓRIA (ES)
const buildCidadeUf = entrega => {
  const cidade = ((entrega && entrega.city) || '').trim()
  const uf = ((entrega && entrega.uf) || '').trim()

  if (cidade && uf) return `${cidade} (${uf})`
  return cidade || uf || ''
}

const INIT_STATE = {
  isLoading: true,
  entregas: [],
  vehicles: [],
  dataPrevista: new Date(),
  height: 200,
  periodo: getPeriodoUltimos30Dias(),
  vehicleStats: {
    qteSku: 0,
    weight: 0,
    steps: 0,
    deliveries: 0,
    ocupation: 0.0,
    qtePallets: 0.0,
    distance: 0,
    valor_nf: 0,
  },
  deliveriesToAssociate: [],
  associatedDeliveries: [],
  filteredEntregas: [],
  zonasEntrega: [],
  zonasEntregaOptions: [],
  selectedZonas: [],
  ufOptions: [],
  selectedUfs: [],
  citiesOptions: [],
  selectedCities: [],
  bairrosOptions: [],
  selectedBairros: [],
  infoComplementarOptions: [],
  selectedInfoComplementar: [],
  empresasOptions: [],
  selectedEmpresas: [],
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  disableSaveButton: false,
  reentregaStatus: null,
  entregasAgendadasStatus: null,
  agendamentosOptions: [],
  selectedAgendamentos: [],
  beepNf: '',
  fleetMapaEmitido: false,
  // Filtro de entregas suspensas (status de liberação): 'ocultar', 'somente' ou null (todas)
  suspendedFilter: null,
}

function suspendedDeliveryMessage(entrega) {
  const motivo = entrega.release_status_reason ? ` Motivo: ${entrega.release_status_reason}.` : ''
  return `A entrega ${entrega.identifier} está suspensa e não pode ser carregada até ser liberada.${motivo}`
}

function CarregamentoVeiculo({ history, selectedTp, dispatch }) {
  // Context
  const { carregamentoVeiculoContext, setCarregamentoVeiculoContext } = useCarregamentoVeiculoContext()

  // Vars
  const [state, setState] = React.useState({
    ...INIT_STATE,
    periodo: carregamentoVeiculoContext.selectedPeriodo || getPeriodoUltimos30Dias(),
  })
  const [rowsPerPage, setRowsPerPage] = useState(carregamentoVeiculoContext.rowsPerPage || 10)
  const [paginatorFirst, setPaginatorFirst] = useState(0)
  const [globalFilter, setGlobalFilter] = React.useState('')
  const [modal, setModal] = useState({ show: false, data: null })
  const [modalTentativaReentrega, setModalTentativaReentrega] = useState({ show: false, data: null })
  // Status de liberação (suspenso/liberado): só exibido para TPs com a opção ligada.
  // Entrega suspensa não pode ser selecionada para carregar; o backend também bloqueia.
  const showReleaseStatus = useShowReleaseStatus(selectedTp)
  const isSuspended = entrega =>
    showReleaseStatus && !!entrega && entrega.release_status === 'suspended'

  // Events
  const onBtnCarregar = async vehicle => {
    try {
      if (vehicle === '') throw new Error('Selecione o Veículo')
      if (state.fleetMapaEmitido) {
        throw new Error(
          'Não é possível adicionar entregas a uma frota com mapa de carga já emitido',
        )
      }
      const filteredEntregas = state.filteredEntregas.map(entrega => {
        if (entrega.selected) {
          entrega.vehicle_badge = vehicle.vehicle_badge
        } else {
          entrega.vehicle_badge = ''
        }
        return entrega
      })
      const entregasSelecionadas = filteredEntregas.filter(
        e => e.vehicle_badge === vehicle.vehicle_badge,
      )

      const { distance } = await postRoutesDistance(
        selectedTp,
        [...state.associatedDeliveries, ...entregasSelecionadas].map(e => ({
          latitude: e.latitude,
          longitude: e.longitude,
        })),
      )
      setState(prevState => ({
        ...state,
        filteredEntregas,
        deliveriesToAssociate: entregasSelecionadas,
        vehicleStats: updateVehicleStats(
          prevState.associatedDeliveries,
          entregasSelecionadas,
          vehicle,
          distance,
        ),
      }))
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    }
  }

  const onBtnSalvar = async () => {
    setState(prevState => ({
      ...prevState,
      loadingMessage: LOAD_MESSAGE.SALVAR_CARREGAMENTO,
      disableSaveButton: true,
    }))
  }
  const salvarCarregamento = async () => {
    try {
      const carregamentos = _buildCarregamento()
      if (carregamentos.length === 0)
        throw new Error('Não existe carregamento para ser salvo. Favor verifique')
      await CarregamentoHttp.saveCarregamento(selectedTp, carregamentos)
      await _fetchInfoIniciais(state.periodo.dataInicial, state.periodo.dataFinal)
      setState(prevState => ({
        ...prevState,
        loadingMessage: LOAD_MESSAGE.SUCCESS,
        disableSaveButton: false,
      }))
    } catch (error) {
      setState(prevState => ({
        ...prevState,
        loadingMessage: LOAD_MESSAGE.NONE,
        errorMessage: errorHandler(error),
        disableSaveButton: false,
      }))
    }
  }

  const onChangeSelecionarTodos = value => {
    setState(prevState => {
      prevState.selecionarTodos = value
      prevState.filteredEntregas = prevState.filteredEntregas.map(tp => ({
        ...tp,
        selected: isSuspended(tp) ? false : value,
      }))
      return { ...prevState }
    })
  }

  const onBeepNfEnter = () => {
    const chave = (state.beepNf || '').trim()

    if (!chave) {
      setState(prevState => ({ ...prevState, beepNf: '' }))
      return
    }
    const index = state.filteredEntregas.findIndex(entrega => entrega.chave_nf === chave)

    if (index === -1) {
      dispatch(showMessageBox(null, 'Nota Fiscal não encontrada'))
      setState(prevState => ({ ...prevState, beepNf: '' }))
      return
    }

    if (isSuspended(state.filteredEntregas[index])) {
      dispatch(showMessageBox(null, suspendedDeliveryMessage(state.filteredEntregas[index])))
      setState(prevState => ({ ...prevState, beepNf: '' }))
      return
    }

    const filteredEntregas = state.filteredEntregas.map((entrega, i) =>
      i === index ? { ...entrega, selected: true } : entrega,
    )

    const selecionarTodos =
      filteredEntregas.length > 0 && filteredEntregas.every(entrega => entrega.selected)

    setState(prevState => ({
      ...prevState,
      filteredEntregas,
      selecionarTodos,
      beepNf: '',
    }))
  }

  const onBtnExportarExcelClick = async () => {
    try {
      dispatch(showLoadingBox('Gerando Excel...'))

      const deliveryStepIds = state.filteredEntregas.map(e => e.step_id).filter(Boolean)
      const porcentagensConferidas = deliveryStepIds.length
        ? await CarregamentoHttp.getPorcentagemConferidaEntregasParaCarregamento(
            selectedTp,
            deliveryStepIds,
          )
        : []
      const porcentagemConferidaByStepId = porcentagensConferidas.reduce((result, item) => {
        result[item.step_id] = item.porcentagem_conferida
        return result
      }, {})

      const colunas = [
        [
          'identifier',
          'nome_fantasia',
          'razao_social',
          'cnpj',
          'uf',
          'cidade',
          'bairro',
          'empresa',
          'qte_cx',
          'Porcentagem Conferida',
          'peso',
          'observacao',
        ],
      ]
      state.filteredEntregas.forEach(e => {
        const porcentagemConferida = Number(porcentagemConferidaByStepId[e.step_id] || 0).toFixed(2)
        colunas.push([
          e.identifier,
          e.nome_fantasia,
          e.razao_social,
          e.cnpj,
          e.uf,
          e.city,
          e.district,
          e.company_name,
          e.qte_sku,
          `${porcentagemConferida}%`,
          e.weight,
          e.observation,
        ])
      })

      gerarArquivoExcel('entregas', colunas, 'entregas_para_carregamento.xlsx')
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    } finally {
      dispatch(hideLoadingBox())
    }
  }

  const onCheckboxClicked = entrega => {
    if (isSuspended(entrega)) {
      dispatch(showMessageBox(null, suspendedDeliveryMessage(entrega)))
      return
    }
    const index = state.filteredEntregas.findIndex(e => e.step_id === entrega.step_id)
    if (index !== -1) {
      const _entregas = []
      state.filteredEntregas.forEach(e => _entregas.push({ ...e }))
      _entregas[index].selected = !_entregas[index].selected
      setState({ ...state, filteredEntregas: _entregas })
    }
  }

  const onMenuItemClicked = (item, entrega) => {
    switch (item) {
      case 'Detalhes':
        setModal({ show: true, data: entrega })
        break

      default:
        break
    }
  }

  const onModalSave = () => {
    setModal({ show: false, data: null })
  }

  // Hooks
  useEffect(() => {
    _fetchInfoIniciais(state.periodo.dataInicial, state.periodo.dataFinal)
  }, [selectedTp])

  useEffect(() => {
    setState({ ...state, height: window.innerHeight - 410 })
  }, [state.filteredEntregas])

  useEffect(() => {
    // Filtra entregas por cidade e reentrega, mas NÃO por bairro ainda
    let entregasFiltradasSemBairro = state.entregas

    if (state.selectedZonas.length) {
      const cityIdsDasZonas = buildCityIdsFromZonas(state.zonasEntrega, state.selectedZonas)
      entregasFiltradasSemBairro = entregasFiltradasSemBairro.filter(entrega =>
        cityIdsDasZonas.has(entrega.city_id),
      )
    }

    if (state.selectedUfs.length) {
      entregasFiltradasSemBairro = entregasFiltradasSemBairro.filter(entrega =>
        state.selectedUfs.includes(entrega.uf),
      )
    }

    const citiesOptions = [...new Set(entregasFiltradasSemBairro.map(e => e.city))]
      .sort()
      .map(city => ({ label: city, value: city }))

    if (state.selectedCities.length) {
      entregasFiltradasSemBairro = entregasFiltradasSemBairro.filter(entrega =>
        state.selectedCities.includes(entrega.city),
      )
    }

    if (state.reentregaStatus === 12) {
      entregasFiltradasSemBairro = entregasFiltradasSemBairro.filter(
        entrega => entrega.status_id === 12,
      )
    } else if (state.reentregaStatus === false) {
      entregasFiltradasSemBairro = entregasFiltradasSemBairro.filter(
        entrega => entrega.status_id !== 12,
      )
    }

    // Filtro Entregas Suspensas (status de liberação)
    if (showReleaseStatus && state.suspendedFilter === 'ocultar') {
      entregasFiltradasSemBairro = entregasFiltradasSemBairro.filter(
        entrega => entrega.release_status !== 'suspended',
      )
    } else if (showReleaseStatus && state.suspendedFilter === 'somente') {
      entregasFiltradasSemBairro = entregasFiltradasSemBairro.filter(
        entrega => entrega.release_status === 'suspended',
      )
    }

    // Filtro Entregas Agendadas
    if (state.entregasAgendadasStatus === 'sim') {
      entregasFiltradasSemBairro = entregasFiltradasSemBairro.filter(
        entrega => entrega.agendamento_entrega != null,
      )
    } else if (state.entregasAgendadasStatus === 'nao') {
      entregasFiltradasSemBairro = entregasFiltradasSemBairro.filter(
        entrega => entrega.agendamento_entrega == null,
      )
    }

    const bairrosOptions = [...new Set(entregasFiltradasSemBairro.map(e => e.district))].map(c => ({
      label: c,
      value: c,
    }))

    const infoComplementarOptions = [
      ...new Set(entregasFiltradasSemBairro.map(e => e.info_complementar).filter(Boolean)),
    ].map(c => ({
      label: c,
      value: c,
    }))

    const empresasOptions = [...new Set(entregasFiltradasSemBairro.map(e => e.company_name).filter(Boolean))]
      .sort()
      .map(e => ({ label: e, value: e }))

    const agendamentosOptions = buildAgendamentosOptions(entregasFiltradasSemBairro)

    let entregasFiltradas = entregasFiltradasSemBairro
    if (state.selectedBairros.length) {
      entregasFiltradas = entregasFiltradas.filter(entrega =>
        state.selectedBairros.includes(entrega.district),
      )
    }

    if (state.selectedInfoComplementar.length) {
      entregasFiltradas = entregasFiltradas.filter(entrega =>
        state.selectedInfoComplementar.includes(entrega.info_complementar),
      )
    }

    if (state.selectedEmpresas.length) {
      entregasFiltradas = entregasFiltradas.filter(entrega =>
        state.selectedEmpresas.includes(entrega.company_name),
      )
    }

    if (state.selectedAgendamentos.length) {
      entregasFiltradas = entregasFiltradas.filter(entrega =>
        matchAgendamentoSelecionado(entrega, state.selectedAgendamentos),
      )
    }

    setState(prevState => ({
      ...prevState,
      filteredEntregas: entregasFiltradas,
      citiesOptions,
      bairrosOptions,
      infoComplementarOptions,
      empresasOptions,
      agendamentosOptions,
    }))
  }, [
    state.entregas,
    state.zonasEntrega,
    state.selectedZonas,
    state.selectedUfs,
    state.selectedCities,
    state.selectedBairros,
    state.selectedInfoComplementar,
    state.selectedEmpresas,
    state.reentregaStatus,
    state.entregasAgendadasStatus,
    state.selectedAgendamentos,
    state.suspendedFilter,
    showReleaseStatus,
  ])

  useEffect(() => {
    switch (state.loadingMessage) {
      case LOAD_MESSAGE.NONE:
        dispatch(hideLoadingBox())
        break

      case LOAD_MESSAGE.SALVAR_CARREGAMENTO:
        dispatch(showLoadingBox(state.loadingMessage, LOAD_MESSAGE.SALVAR_CARREGAMENTO))
        //salvarCarregamento()
        break

      case LOAD_MESSAGE.SUCCESS:
        dispatch(hideLoadingBox())
        dispatch(showMessageBox(LOAD_MESSAGE.SUCCESS, null))
        break

      default:
        break
    }
  }, [state.loadingMessage])

  useEffect(() => {
    if (state.errorMessage) {
      dispatch(showMessageBox(null, state.errorMessage))
    }
  }, [state.errorMessage])

  //Helpers
  const _fetchInfoIniciais = async (pInicial, pFinal) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, periodo: { dataInicial: pInicial, dataFinal: pFinal } }))
      setCarregamentoVeiculoContext(prev => ({
        ...prev,
        selectedPeriodo: { dataInicial: pInicial, dataFinal: pFinal },
      }))
      const [info, zonasEntrega] = await Promise.all([
        CarregamentoHttp.getCarregamentoInfoIniciais(
          selectedTp,
          parseToMysqlFormat(pInicial),
          parseToMysqlFormat(pFinal),
        ),
        selectedTp ? getZonasEntrega(selectedTp).catch(() => []) : [],
      ])
      const { entregas: entregasApi, vehicles } = info

      // Campo derivado da coluna unificada Cidade (UF). Fica no registro para que a
      // busca global da tabela continue encontrando tanto a cidade quanto o estado.
      const entregas = entregasApi.map(entrega => ({ ...entrega, city_uf: buildCidadeUf(entrega) }))

      const ufOptions = [...new Set(entregas.map(e => e.uf).filter(Boolean))].sort().map(sigla => ({
        label: ESTADOS[sigla] || sigla,
        value: sigla,
      }))
      const citiesOptions = [...new Set(entregas.map(e => e.city).sort())].map(c => ({
        label: c,
        value: c,
      }))
      const infoComplementarOptions = [
        ...new Set(entregas.map(e => e.info_complementar).filter(Boolean)),
      ].map(c => ({
        label: c,
        value: c,
      }))

      setState(prev => ({
        ...prev,
        isLoading: false,
        entregas,
        filteredEntregas: entregas,
        vehicles,
        periodo: { dataInicial: pInicial, dataFinal: pFinal },
        deliveriesToAssociate: [],
        associatedDeliveries: [],
        vehicleStats: { ...INIT_STATE.vehicleStats },
        zonasEntrega,
        zonasEntregaOptions: buildZonasEntregaOptions(zonasEntrega),
        selectedZonas: [],
        ufOptions,
        selectedUfs: [],
        citiesOptions,
        selectedCities: [],
        bairrosOptions: [],
        selectedBairros: [],
        infoComplementarOptions,
        selectedInfoComplementar: [],
        empresasOptions: [],
        selectedEmpresas: [],
        reentregaStatus: null,
        entregasAgendadasStatus: null,
        suspendedFilter: null,
        agendamentosOptions: [],
        selectedAgendamentos: [],
      }))
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        periodo: { dataInicial: pInicial, dataFinal: pFinal },
      }))
      dispatch(showMessageBox(null, errorHandler(error)))
    } finally {
      setState(prevState => ({ ...prevState, isLoading: false }))
    }
  }

  const onLimparFiltros = () => {
    setState(prevState => ({
      ...prevState,
      selectedZonas: [],
      selectedUfs: [],
      selectedCities: [],
      selectedBairros: [],
      selectedInfoComplementar: [],
      selectedEmpresas: [],
      reentregaStatus: null,
      entregasAgendadasStatus: null,
      suspendedFilter: null,
      selectedAgendamentos: [],
      beepNf: '',
    }))
  }

  const onPageChange = e => {
    setPaginatorFirst(e.first)
    if (e.rows !== rowsPerPage) {
      setRowsPerPage(e.rows)
      setCarregamentoVeiculoContext(prev => ({ ...prev, rowsPerPage: e.rows }))
    }
  }

  const _buildCarregamento = () => {
    const associations = []
    const entregasSelecionadas = state.filteredEntregas.filter(c => c.vehicle_badge !== '')
    const placas = new Set(entregasSelecionadas.map(e => e.vehicle_badge))

    placas.forEach(p => {
      const veiculo = state.vehicles.find(v => v.vehicle_badge === p)
      if (veiculo) {
        const entregas = entregasSelecionadas.filter(assoc => assoc.vehicle_badge === p)
        associations.push({
          vehicle_id: veiculo.id,
          driver_id: veiculo.default_driver_id,
          expected_date: parseToMysqlFormat(state.dataPrevista),
          delivery_step_ids: [...new Set(entregas.map(s => s.step_id))],
        })
      }
    })

    return associations
  }

  const getRouteDistance = async (tp_id, deliveries) => {
    setState({
      ...state,
      isLoading: false,
    })
    const waypoints = deliveries.map(delivery => ({
      latitude: delivery.latitude,
      longitude: delivery.longitude,
    }))

    const { distance } = await postRoutesDistance(tp_id, waypoints)

    setState(prevState => ({
      ...prevState,
      vehicleStats: {
        ...prevState.vehicleStats,
        distance: distance ? distance / 1000 : 0,
      },
    }))
  }

  const updateVehicleStats = (associatedDeliveries, deliveriesToAssociate, vehicle) => {
    if ((associatedDeliveries.length > 0 || deliveriesToAssociate.length) && vehicle) {
      const vehicleStats = {
        ...INIT_STATE.vehicleStats,
      }
      const deliveries = [...associatedDeliveries, ...deliveriesToAssociate]
      deliveries.forEach(delivery => {
        vehicleStats.qteSku += delivery.qte_sku
        vehicleStats.deliveries++
        vehicleStats.weight += delivery.weight
        vehicleStats.valor_nf += delivery.valor_nf || 0
        if (vehicleStats.qtePallets != null && delivery.qte_total_pallets)
          vehicleStats.qtePallets += delivery.qte_total_pallets
        else vehicleStats.qtePallets = null
      })
      vehicleStats.ocupation = (vehicleStats.weight * 100) / vehicle.capacity_kg
      const customersIds = new Set(deliveries.map(delivery => delivery.client_id))
      vehicleStats.steps = customersIds.size
      return vehicleStats
    } else
      return {
        ...INIT_STATE.vehicleStats,
      }
  }

  useEffect(() => {
    if (state.disableSaveButton) {
      salvarCarregamento()
    }
  }, [state.disableSaveButton])

  const farolTemplate = rowData => {
    const isReentrega = rowData.status_id === 12
    const color = isReentrega ? laranja : null
    const hasAttemptDate = isReentrega && rowData.last_attempt_date

    const farolCircle = (
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: color,
          margin: '0 auto',
        }}
      />
    )

    if (!isReentrega) return farolCircle

    return (
      <div
        style={{
          cursor: hasAttemptDate ? 'pointer' : 'default',
          textAlign: 'center',
          display: 'inline-block',
        }}
        onClick={() => {
          if (hasAttemptDate) {
            setModalTentativaReentrega({
              show: true,
              data: {
                delivery_event_id: rowData.event_id,
                last_attempt_date: rowData.last_attempt_date,
                last_attempt_justify: rowData.last_attempt_justify,
              },
            })
          }
        }}
        title={hasAttemptDate ? 'Clique para ver a tentativa de reentrega' : 'Reentrega'}
      >
        {farolCircle}
        {hasAttemptDate && (
          <div style={{ marginTop: 4 }}>
            <strong style={{ fontSize: '0.68rem', color: '#333' }}>Data última tentativa: </strong>
            <strong style={{ fontSize: '0.68rem', color: '#333' }}>
              {new Date(rowData.last_attempt_date).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }).replace(' ', ' ')}
            </strong>
          </div>
        )}
      </div>
    )
  }

  // Render
  return (
    <>
      <PageHeader>3.4 - Carregamento de Veículos de Entregas</PageHeader>
      <div style={{ marginTop: 20 }}>
        <CustomPanel showBtnNovo={false} onVoltar={history.goBack}>
          {state.isLoading ? (
            <div className="text-center">
              <Loading size="md" />
            </div>
          ) : (
            <>
              <div className="p-grid">
                <div className="p-col-3">
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
                    Data de importação das Entregas:
                  </label>
                  <Periodo
                    periodoInicial={state.periodo.dataInicial}
                    periodoFinal={state.periodo.dataFinal}
                    onPesquisar={(pInicial, pFinal) => _fetchInfoIniciais(pInicial, pFinal)}
                  />
                </div>

                <div className="p-col-12">
                  <CustomCheckbox
                    checked={state.selecionarTodos}
                    onChange={() => onChangeSelecionarTodos(!state.selecionarTodos)}
                  >
                    Selecionar Todos
                  </CustomCheckbox>
                </div>
              </div>
              <CarregametoForm
                vehicles={state.vehicles}
                onBtnCarregarClicked={onBtnCarregar}
                onBtnSalvar={onBtnSalvar}
                onDataPrevistaChange={data => setState({ ...state, dataPrevista: data })}
                onBtnExportarExcel={onBtnExportarExcelClick}
                selectedTp={selectedTp}
                entregas={state.filteredEntregas}
                periodo={state.periodo}
                setState={setState}
                disableSaveButton={state.disableSaveButton}
                updateVehicleStats={updateVehicleStats}
              />
              <div className="p-grid" style={{ marginTop: '15px' }}>
                <div className="p-col-2">
                  <CustomCardColorBar
                    value={state.vehicleStats.steps}
                    title={'Total de Paradas'}
                    subTitle={`un`}
                    barColor={vinho}
                  />
                </div>
                <div className="p-col-2">
                  <CustomCardColorBar
                    value={state.vehicleStats.deliveries}
                    title={'Total de Entregas'}
                    subTitle={`un`}
                    barColor={laranja}
                  />
                </div>
                <div className="p-col-2">
                  <CustomCardColorBar
                    value={state.vehicleStats.qteSku}
                    title={'Qte Volumes'}
                    subTitle={`qte`}
                    barColor={azul_escuro}
                  />
                </div>
                <div className="p-col-2">
                  <CustomCardColorBar
                    value={
                      <NumeroDecimal decimalScale={2}>{state.vehicleStats.weight}</NumeroDecimal>
                    }
                    title={'Peso'}
                    subTitle={`kg`}
                    barColor={azul_hover}
                  />
                </div>
                <div className="p-col-2">
                  <CustomCardColorBar
                    value={state.vehicleStats.ocupation.toFixed(2)}
                    title={'Tx. Ocupação KG'}
                    subTitle={`%`}
                    barColor={pink}
                  />
                </div>
                <div className="p-col-2">
                  <CustomCardColorBar
                    value={
                      state.vehicleStats.qtePallets ? Math.ceil(state.vehicleStats.qtePallets) : '-'
                    }
                    title={'Pallets'}
                    subTitle={`qte`}
                    barColor={gray}
                  />
                </div>

                <div className="p-col-2">
                  <CustomCardDistance
                    value={
                      <NumeroDecimal decimalScale={0}>{state.vehicleStats.distance}</NumeroDecimal>
                    }
                    title={'Distância Total'}
                    subTitle={`km`}
                    barColor={vinho}
                    btn={
                      <Btn onClick={() => getRouteDistance(selectedTp, state.associatedDeliveries)}>
                        Obter Distância
                      </Btn>
                    }
                    selectedTp={selectedTp}
                  />
                </div>
                <div className="p-col-2">
                  <CustomCardColorBar
                    value={
                      <NumeroDecimal decimalScale={2}>{state.vehicleStats.valor_nf}</NumeroDecimal>
                    }
                    title={'Faturamento'}
                    subTitle={`R$`}
                    barColor={laranja}
                  />
                </div>
              </div>
              <Fieldset legend="Filtro" toggleable collapsed={true} className="p-col-12">
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Zona de Entrega"
                      options={state.zonasEntregaOptions}
                      value={state.selectedZonas}
                      onChange={zonas =>
                        setState(prevState => ({ ...prevState, selectedZonas: zonas }))
                      }
                    />
                  </div>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Estados"
                      options={state.ufOptions}
                      value={state.selectedUfs}
                      onChange={ufs => setState(prevState => ({ ...prevState, selectedUfs: ufs }))}
                    />
                  </div>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Cidades"
                      options={state.citiesOptions}
                      value={state.selectedCities}
                      onChange={cities =>
                        setState(prevState => ({ ...prevState, selectedCities: cities }))
                      }
                    />
                  </div>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Bairros"
                      options={state.bairrosOptions}
                      value={state.selectedBairros}
                      onChange={bairros =>
                        setState(prevState => ({ ...prevState, selectedBairros: bairros }))
                      }
                    />
                  </div>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Info Complementar"
                      options={state.infoComplementarOptions}
                      value={state.selectedInfoComplementar}
                      onChange={infoComplementar =>
                        setState(prevState => ({
                          ...prevState,
                          selectedInfoComplementar: infoComplementar,
                        }))
                      }
                    />
                  </div>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Empresa"
                      options={state.empresasOptions}
                      value={state.selectedEmpresas}
                      onChange={empresas =>
                        setState(prevState => ({ ...prevState, selectedEmpresas: empresas }))
                      }
                    />
                  </div>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomDropdown
                      label="Mostrar Reentregas"
                      options={[
                        { label: 'Sim', value: 12 },
                        { label: 'Não', value: false },
                        { label: 'Todos', value: null },
                      ]}
                      value={state.reentregaStatus}
                      onChange={value =>
                        setState(prevState => ({ ...prevState, reentregaStatus: value }))
                      }
                    />
                  </div>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomDropdown
                      label="Entregas Agendadas"
                      options={[
                        { label: 'Sim', value: 'sim' },
                        { label: 'Não', value: 'nao' },
                        { label: 'Todos', value: null },
                      ]}
                      value={state.entregasAgendadasStatus}
                      onChange={value =>
                        setState(prevState => ({
                          ...prevState,
                          entregasAgendadasStatus: value,
                        }))
                      }
                    />
                  </div>
                  {showReleaseStatus && (
                    <div className="p-col-2" style={{ minWidth: 200 }}>
                      <CustomDropdown
                        label="Entregas Suspensas"
                        options={[
                          { label: 'Ocultar', value: 'ocultar' },
                          { label: 'Somente suspensas', value: 'somente' },
                          { label: 'Todas', value: null },
                        ]}
                        value={state.suspendedFilter}
                        onChange={value =>
                          setState(prevState => ({ ...prevState, suspendedFilter: value }))
                        }
                      />
                    </div>
                  )}
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Dt. Próx. Agendamento"
                      options={state.agendamentosOptions}
                      value={state.selectedAgendamentos}
                      onChange={agendamentos =>
                        setState(prevState => ({ ...prevState, selectedAgendamentos: agendamentos }))
                      }
                    />
                  </div>
                  <div style={{ paddingTop: 27 , marginBottom: 10}}>
                        <Btn onClick={onLimparFiltros}>Limpar Filtros</Btn>
                  </div>
                  <div className="p-col-4" style={{ minWidth: 200 }}>
                    <InputText
                      marginTop={10}
                      label="Beep NF"
                      value={state.beepNf}
                      onChange={value =>
                        setState(prevState => ({
                          ...prevState,
                          beepNf: value,
                        }))
                      }
                      onEnter={onBeepNfEnter}
                    />
                  </div>
                </div>
              </Fieldset>
              <div style={{ marginTop: 20 }}>
                <DataTable
                  value={state.filteredEntregas}
                  header={<CustomDataTableHeader onChange={value => setGlobalFilter(value)} />}
                  globalFilter={globalFilter}
                  rows={rowsPerPage}
                  first={paginatorFirst}
                  paginator={true}
                  rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
                  onPage={onPageChange}
                  emptyMessage={'Não existe entrega pendente para carregamento'}
                >
                  <Column
                    field="selected"
                    style={tableCellCentered(60)}
                    body={entrega =>
                      isSuspended(entrega) ? (
                        <span
                          title={suspendedDeliveryMessage(entrega)}
                          style={{ cursor: 'not-allowed' }}
                        >
                          —
                        </span>
                      ) : (
                        <CustomTriStateCheckbox
                          value={entrega && entrega.selected ? entrega.selected : false}
                          onClick={() => onCheckboxClicked(entrega)}
                        />
                      )
                    }
                  />
                  <Column field="identifier" header="Identificador" />
                  <Column field="nome_fantasia" header="Nome Fantasia" />
                  <Column
                    field="cnpj"
                    header="CNPJ/CPF"
                    body={({ cnpj }) => <Cnpj>{cnpj}</Cnpj>}
                    style={tableCellCentered(150)}
                  />
                  <Column field="company_name" header="Empresa" style={tableCellCentered()} />
                  <Column
                    field="city_uf"
                    header="Cidade (UF)"
                    style={tableCellCentered(180)}
                    body={entrega => (entrega ? buildCidadeUf(entrega) : '')}
                  />
                  <Column field="district" header="Bairro" style={tableCellCentered(150)} />
                  <Column field="weight" header="Peso (Kg)" style={tableCellCentered()} />
                  <Column
                    field="qte_sku"
                    style={tableCellCentered()}
                    header="Qte. SKUs"
                    body={entrega => (
                      <span title={entrega && entrega.grupos_produtos ? entrega.grupos_produtos : undefined}>
                        {entrega ? entrega.qte_sku : ''}
                      </span>
                    )}
                  />
                  <Column field="valor_nf" header="Valor" style={tableCellCentered()} />
                  <Column
                    field="vehicle_badge"
                    header="Placa"
                    style={tableCellCentered()}
                    body={entrega => {
                      return entrega && entrega.vehicle_badge ? (
                        <span style={{ color: 'red' }}>{entrega.vehicle_badge}</span>
                      ) : (
                        ''
                      )
                    }}
                  />
                  <Column
                    field="status_id"
                    header="Reentrega"
                    style={tableCellCentered()}
                    body={farolTemplate}
                  />
                  <Column
                    field="proxima_data_agendamento"
                    header="Dt. Próx. Agendamento"
                    style={tableCellCentered(160)}
                    body={entrega => (entrega ? entrega.proxima_data_agendamento || '' : '')}
                  />
                  <Column
                    header="Menu"
                    style={tableCellCentered(70)}
                    body={delivery => (
                      <ThreeDots
                        items={['Detalhes']}
                        onClick={item => onMenuItemClicked(item, delivery)}
                      />
                    )}
                    bodyStyle={tableCellCentered()}
                  />
                </DataTable>
              </div>
            </>
          )}
        </CustomPanel>
      </div>
      <ModalEditarCarregamento
        show={modal.show}
        data={modal.data}
        onModalClose={() => {
          setModal({ show: false, data: null })
        }}
        onModalSave={onModalSave}
      />
      <ModalTentativaReentrega
        show={modalTentativaReentrega.show}
        entrega={modalTentativaReentrega.data}
        readOnly={true}
        onModalClose={() => setModalTentativaReentrega({ show: false, data: null })}
        onModalSave={() => setModalTentativaReentrega({ show: false, data: null })}
      />
    </>
  )
}

function CarregamentoFormElement({
  history,
  vehicles,
  onBtnCarregarClicked,
  onBtnSalvar,
  onBtnExportarExcel,
  onDataPrevistaChange,
  dispatch,
  selectedTp,
  entregas,
  periodo,
  setState,
  updateVehicleStats,
  disableSaveButton,
}) {
  const [selectedVehicle, setSelectedVehicle] = useState('')
  const [dataPrevista, setDataPrevista] = useState(new Date())

  // Events
  const onCmbDataPrevistaChange = data => {
    setDataPrevista(data)
    onDataPrevistaChange(data)
    onSelectedVeiculoChange(selectedVehicle, selectedTp, data)
  }

  const onBtnImportarRoteiro = () => {
    typeof dataPrevista !== 'object' &&
      dispatch(showMessageBox(null, 'Data Prevista de Entrega Incorreta'))
    history.push('/main/entregas/importacao-roteiro', { dataPrevista, periodo })
  }

  const onSelectedVeiculoChange = (value, tp_id, expected_date) => {
    setSelectedVehicle(value)
    getEnviosParaFrota(tp_id, value.vehicle_badge, parseToMysqlFormat(expected_date))
      .then(fleet => {
        setState(prevState => ({
          ...prevState,
          associatedDeliveries: fleet.deliveries,
          vehicleStats: updateVehicleStats(
            fleet.deliveries,
            prevState.deliveriesToAssociate,
            value,
          ),
          deliveriesToAssociate: [],
          fleetMapaEmitido: fleet.mapa_emitido || false,
        }))
      })
      .catch(() =>
        setState(prevState => ({
          ...prevState,
          vehicleStats:
            prevState.deliveriesToAssociate.length > 0
              ? updateVehicleStats([], prevState.deliveriesToAssociate, value)
              : { ...INIT_STATE.vehicleStats },
          associatedDeliveries: [],
          fleetMapaEmitido: false,
        })),
      )
  }

  // Rendering
  return (
    <div>
      <Fieldset legend="Selecione o Veículo que será carregado">
        <div className="p-grid">
          <div className="p-col-2">
            <CustomCalendar
              marginTop={0}
              label="Data Prevista de Entrega"
              value={dataPrevista}
              onChange={onCmbDataPrevistaChange}
            />
          </div>
          <div className="p-col-2">
            <CustomDropdown
              marginTop={0}
              filter={true}
              value={selectedVehicle}
              label="Veículo"
              optionLabel="vehicle_badge"
              options={vehicles}
              onChange={value => onSelectedVeiculoChange(value, selectedTp, dataPrevista)}
            />
          </div>
          <div className="p-col-6" style={{ paddingTop: 22, whiteSpace: 'nowrap' }}>
            <Btn onClick={() => onBtnCarregarClicked(selectedVehicle)}>Carregar</Btn>
            <div style={{ display: 'inline-block', marginLeft: 10 }}>
              <Btn onClick={onBtnSalvar} disabled={disableSaveButton}>
                Salvar
              </Btn>
            </div>
            <div style={{ display: 'inline-block', marginLeft: 10 }}>
              <Btn onClick={onBtnImportarRoteiro}>Importar Roteiro</Btn>
            </div>
            <div style={{ display: 'inline-block', marginLeft: 10 }}>
              <Btn onClick={onBtnExportarExcel}>Exportar Excel</Btn>
            </div>
            <div style={{ display: 'inline-block', marginLeft: 10 }}>
              <Btn
                onClick={() => {
                  if (selectedTp && entregas.length > 0)
                    history.push('/main/entregas/mapa-carregamento', {
                      vehicles: vehicles,
                      selectedTp: selectedTp,
                      periodo: periodo,
                    })
                  else
                    dispatch(
                      showMessageBox(
                        null,
                        'É necessário que um TP esteja selecionado, e que existam entregas para associação.',
                      ),
                    )
                }}
              >
                Ver Mapa
              </Btn>
            </div>
            {isRouteAllowed(144) && (
              <div style={{ display: 'inline-block', marginLeft: 10 }}>
                <Btn
                  onClick={() => {
                    if (selectedTp && entregas.length > 0)
                      history.push('/main/entregas/roteirizacao-automatica', {
                        vehicles,
                        entregas,
                        periodo,
                        dataPrevista,
                      })
                    else
                      dispatch(
                        showMessageBox(
                          null,
                          'É necessário que um TP esteja selecionado, e que existam entregas disponíveis.',
                        ),
                      )
                  }}
                >
                  Roteirização Automática
                </Btn>
              </div>
            )}
          </div>
        </div>
      </Fieldset>
    </div>
  )
}

const mapStateToProps = ({ system: { selectedTp } }) => ({ selectedTp })

const CarregametoForm = connect()(withRouter(CarregamentoFormElement))
export default connect(mapStateToProps)(withRouter(CarregamentoVeiculo))
